import type { PushConfigDto } from '@ukinfo/shared';
import { api } from './api';

/**
 * Push auf diesem Geraet.
 *
 * Zwei Arten der Anmeldung teilen sich denselben Ablauf: ein Zugang
 * (`account`) oder das Geraet eines persoenlichen Sport-Links (`link`).
 * Nur die Serveradresse unterscheidet sich.
 */
export type PushChannel = 'account' | 'link';

export type PushState =
  | 'unsupported'
  /** iPhone/iPad: erst nach "Zum Home-Bildschirm" moeglich. */
  | 'needs-install'
  | 'denied'
  | 'off'
  | 'on';

const ENDPOINTS: Record<PushChannel, string> = {
  account: '/api/push/subscriptions',
  link: '/api/public/sports-access/push-subscriptions',
};

export function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS meldet sich als Mac - verraet sich aber ueber den Touchscreen.
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function supported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  return existing ?? navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration('/');
  const subscription = await reg?.pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' ? 'on' : 'off';
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Fragt nach der Erlaubnis und meldet das Geraet beim Server an. */
export async function enablePush(channel: PushChannel): Promise<PushState> {
  if (!supported()) return pushState();
  const config = await api.get<PushConfigDto>('/api/push/config');
  if (!config.enabled || !config.publicKey) throw new Error('Push ist auf dem Server nicht eingerichtet');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const reg = await registration();
  await navigator.serviceWorker.ready;
  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(config.publicKey),
    });
  }
  await api.post(ENDPOINTS[channel], subscription.toJSON());
  return 'on';
}

/** Meldet dieses Geraet ab - beim Server und im Browser. */
export async function disablePush(channel: PushChannel): Promise<void> {
  if (!supported()) return;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const subscription = await reg?.pushManager.getSubscription();
  if (!subscription) return;
  try {
    await api.del(ENDPOINTS[channel], { endpoint: subscription.endpoint });
  } finally {
    await subscription.unsubscribe();
  }
}

/**
 * Bereits erlaubt? Dann die Anmeldung beim Server auffrischen - Browser
 * tauschen die Adresse gelegentlich aus.
 */
export async function refreshPush(channel: PushChannel): Promise<void> {
  if (!supported() || Notification.permission !== 'granted') return;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const subscription = await reg?.pushManager.getSubscription();
  if (subscription) await api.post(ENDPOINTS[channel], subscription.toJSON());
}
